import { useEffect, useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { IconButton } from './Button'
import './modal.css'

/**
 * Dialog für Anlege- und Bearbeitungsmasken.
 *
 * Bewusst auf `<dialog>` gebaut statt auf ein selbstgebautes Overlay: der
 * Browser übernimmt damit Fokusfalle, Escape und die oberste Ebene, und das
 * sind genau die drei Dinge, die bei Handarbeit regelmäßig fehlen.
 *
 * Ein Portal ist trotzdem nötig — die Shell setzt `isolation: isolate` und
 * `backdrop-filter`, und beides erzeugt einen neuen Stapelkontext, in dem ein
 * Dialog sonst unter der Kopfleiste läge.
 */

export interface ModalProps {
  open: boolean
  title: string
  description?: string
  onClose: () => void
  children: ReactNode
  footer?: ReactNode
  /** Breitere Fassung für Formulare mit zwei Spalten. */
  wide?: boolean
}

function CloseIcon() {
  return (
    <svg viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true">
      <path
        d="m4 4 8 8M12 4l-8 8"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  )
}

export function Modal({
  open,
  title,
  description,
  onClose,
  children,
  footer,
  wide,
}: ModalProps) {
  const ref = useRef<HTMLDialogElement>(null)

  useEffect(() => {
    const dialog = ref.current
    if (!dialog) return

    if (open && !dialog.open) {
      dialog.showModal()
    } else if (!open && dialog.open) {
      dialog.close()
    }
  }, [open])

  // Escape löst `cancel` aus, nicht `close` — ohne diesen Abgriff schlösse der
  // Browser den Dialog, während der React-Zustand ihn weiter für offen hielte.
  useEffect(() => {
    const dialog = ref.current
    if (!dialog) return

    const onCancel = (event: Event) => {
      event.preventDefault()
      onClose()
    }
    dialog.addEventListener('cancel', onCancel)
    return () => dialog.removeEventListener('cancel', onCancel)
  }, [onClose])

  if (typeof document === 'undefined') return null

  return createPortal(
    <dialog
      ref={ref}
      className="modal"
      data-wide={wide || undefined}
      aria-labelledby="modal-title"
      // Klick auf den Hintergrund schließt. Der Treffer wird am Dialog selbst
      // gemessen: alles innerhalb liegt in .modal__panel.
      onClick={(event) => {
        if (event.target === ref.current) onClose()
      }}
    >
      <div className="modal__panel">
        <header className="modal__header">
          <div className="modal__heading">
            <h2 className="modal__title" id="modal-title">
              {title}
            </h2>
            {description && <p className="modal__description">{description}</p>}
          </div>
          <IconButton label="Schließen" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </header>

        <div className="modal__body">{children}</div>

        {footer && <footer className="modal__footer">{footer}</footer>}
      </div>
    </dialog>,
    document.body,
  )
}
